import * as React from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

const LS_KEY = 'teivor.adminWorkspaceId';

interface WorkspaceContextValue {
  /** The currently selected (or entered) workspace id, null = system scope. */
  selectedWorkspaceId: string | null;
  setSelectedWorkspaceId: (id: string | null) => void;
  /**
   * True when the user has explicitly entered a workspace (workspace context).
   * False = system context (picker + system-level nav).
   */
  isInsideWorkspace: boolean;
  /** Enter workspace context for the given id. */
  enterWorkspace: (id: string) => void;
  /** Leave workspace context back to system scope. */
  leaveWorkspace: () => void;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue | null>(null);

function readFromStorage(): string | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function writeToStorage(id: string | null): void {
  try {
    if (id === null) {
      localStorage.removeItem(LS_KEY);
    } else {
      localStorage.setItem(LS_KEY, id);
    }
  } catch {
    // ignore storage errors (private browsing, quota, etc.)
  }
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Derive initial value: URL param wins over localStorage.
  const [selectedWorkspaceId, setSelectedWorkspaceIdState] = React.useState<string | null>(() => {
    const fromUrl = searchParams.get('ws');
    if (fromUrl && fromUrl.length > 0) return fromUrl;
    return readFromStorage();
  });

  // Workspace context is ROUTE-derived (not persisted): true only on /ws/* routes.
  // Keeps the picker + every system route in system context even when a workspace
  // was previously entered — no stale "inside workspace" leak onto the picker.
  const isInsideWorkspace =
    location.pathname === '/ws' || location.pathname.startsWith('/ws/');

  const setSelectedWorkspaceId = React.useCallback((id: string | null) => {
    setSelectedWorkspaceIdState(id);
    writeToStorage(id);

    // Update URL without triggering a navigation/re-render cycle.
    const url = new URL(window.location.href);
    if (id === null) {
      url.searchParams.delete('ws');
    } else {
      url.searchParams.set('ws', id);
    }
    window.history.replaceState(null, '', url.toString());
  }, []);

  const enterWorkspace = React.useCallback((id: string) => {
    // Remember the selected workspace; the caller navigates to /ws/*, which flips
    // isInsideWorkspace via the pathname.
    setSelectedWorkspaceIdState(id);
    writeToStorage(id);

    const url = new URL(window.location.href);
    url.searchParams.set('ws', id);
    window.history.replaceState(null, '', url.toString());
  }, []);

  const leaveWorkspace = React.useCallback(() => {
    // Keep selectedWorkspaceId so the picker can remember which one was last used,
    // but clear the URL ws param so the system context URL is clean. The caller
    // navigates away from /ws/*, which flips isInsideWorkspace via the pathname.
    const url = new URL(window.location.href);
    url.searchParams.delete('ws');
    window.history.replaceState(null, '', url.toString());
  }, []);

  // On mount, if URL had ?ws=, persist the selected id to localStorage too.
  React.useEffect(() => {
    const fromUrl = searchParams.get('ws');
    if (fromUrl && fromUrl.length > 0) {
      writeToStorage(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = React.useMemo(
    () => ({
      selectedWorkspaceId,
      setSelectedWorkspaceId,
      isInsideWorkspace,
      enterWorkspace,
      leaveWorkspace,
    }),
    [selectedWorkspaceId, setSelectedWorkspaceId, isInsideWorkspace, enterWorkspace, leaveWorkspace],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

function useWorkspaceContext(): WorkspaceContextValue {
  const ctx = React.useContext(WorkspaceContext);
  if (ctx === null) {
    throw new Error('useWorkspaceContext must be used inside <WorkspaceProvider>');
  }
  return ctx;
}

export function useSelectedWorkspaceId(): string | null {
  return useWorkspaceContext().selectedWorkspaceId;
}

export function useSetSelectedWorkspaceId(): (id: string | null) => void {
  return useWorkspaceContext().setSelectedWorkspaceId;
}

export function useIsInsideWorkspace(): boolean {
  return useWorkspaceContext().isInsideWorkspace;
}

export function useEnterWorkspace(): (id: string) => void {
  return useWorkspaceContext().enterWorkspace;
}

export function useLeaveWorkspace(): () => void {
  return useWorkspaceContext().leaveWorkspace;
}

export { useWorkspaceContext };
